import { Box, Button, Chip, Grid2 as Grid, Typography } from '@mui/material';
import { useCallback, useMemo } from 'react';
import { UserFilters } from '@/components/Modals/UserFilters';
import type { FiltersProps } from '@/pages/ValuationsV2/components/Filters';
import { FILTER_LABEL_MAP } from '@/pages/ValuationsV2/components/Filters/filterConfig';
import { VALUATIONS_CONTEXT, VALUATIONS_GOOGLE_ANALYTICS_TAG } from '../../constants';
import { useValuationsStore, type ValuationsFilters } from '../../store/valuationsStore';

const chipsContainerStyles = {
  padding: '0 12px',
};

const chipsLabelStyles = {
  paddingRight: '1.5625rem',
};

const chipsActionsStyles = {
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: '0.5rem',
};

type ChipData = {
  key: keyof ValuationsFilters;
  label: string;
  id: string | number;
};

type ChipItemProps = {
  chip: ChipData;
  onDeleteChip?: (chip: ChipData) => void;
};

const ChipItem = ({ chip, onDeleteChip }: ChipItemProps) => {
  const handleDelete = onDeleteChip ? () => onDeleteChip(chip) : undefined;

  return (
    <Chip
      label={chip.label}
      {...(handleDelete && { onDelete: handleDelete })}
      data-id='valuations-filter-chip'
    />
  );
};

const ChipsContainer = ({ filters, setFilters }: Omit<FiltersProps, 'children'>) => {
  const clearFilters = useValuationsStore.use.clearFilters();

  const chips = useMemo<ChipData[]>(() => {
    const filtersArray = Object.entries(filters) as [
      keyof ValuationsFilters,
      ValuationsFilters[keyof ValuationsFilters],
    ][];

    const filterThatValuesAreArrays = filtersArray.filter(
      ([, value]) => Array.isArray(value) && value.length > 0,
    );

    return filterThatValuesAreArrays
      .flatMap(([filterKey, value]): ChipData[] => {
        if (typeof value === 'boolean' || typeof value === 'string' || value === null) {
          return [];
        }
        return (
          value?.map(val => {
            const labelFirstSegment = FILTER_LABEL_MAP[filterKey] || String(filterKey);
            const labelSecondSegment =
              'displayName' in val ? val.displayName : 'name' in val ? val.name : val;

            const id =
              'id' in val && val.id != null ? String(val.id) : `${filterKey}-${labelSecondSegment}`;

            return {
              key: filterKey,
              label: `${labelFirstSegment}:${labelSecondSegment}`,
              id,
            } satisfies ChipData;
          }) ?? []
        );
      })
      .reverse();
  }, [filters]);

  const onDeleteChip = useCallback(
    (chip: ChipData) => {
      setFilters(currentFilters => {
        const currentValue = currentFilters[chip.key];
        if (!Array.isArray(currentValue)) return currentFilters;

        return {
          ...currentFilters,
          [chip.key]: currentValue.filter(v => {
            if (!('id' in v) || v.id == null) return true;
            return String(v.id) !== String(chip.id);
          }),
        };
      });
    },
    [setFilters],
  );

  return (
    <Box
      display='flex'
      flexBasis='auto'
      justifyContent='space-between'
      alignItems='center'
      gap='1rem'
      sx={chipsContainerStyles}
      data-id='valuations-filter-chips-container'
    >
      <Grid container spacing={1} alignItems='center' data-id='valuations-filter-chips-grid'>
        <Typography
          color='secondary'
          variant='body1'
          sx={chipsLabelStyles}
          data-id='valuations-filter-chips-label'
        >
          CURRENT FILTERS
        </Typography>
        {chips.map(chip => (
          <ChipItem
            key={`chip-item-${chip.label}-${chip.id}`}
            chip={chip}
            onDeleteChip={onDeleteChip}
          />
        ))}
      </Grid>

      <Box sx={chipsActionsStyles} data-id='valuations-filter-chips-actions'>
        <Button
          variant='text'
          onClick={clearFilters}
          color='secondary'
          data-id='valuations-filter-chips-clear'
        >
          Clear
        </Button>
        <UserFilters
          filters={filters}
          setFilters={setFilters}
          context={VALUATIONS_CONTEXT}
          googleAnalyticsTag={VALUATIONS_GOOGLE_ANALYTICS_TAG}
        />
      </Box>
    </Box>
  );
};

export { ChipsContainer };
